import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  alpha,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import axiosInstance from '../../api/axiosConfig';
import EmployeeBankDetails from './EmployeeBankDetails';
import EmployeeIdentifier from './EmployeeIdentifier';
import EmployeeSalaryAssignment from './EmployeeSalaryAssignment';
import {
  EmbeddedSection,
  EmployeeHeroSection,
  EmployeeProfileTableSection,
  EmployeeWorkSummarySection,
} from './EmployeeProfileSections';
import PageContainer from '../common/PageContainer';
import PageHeader from '../common/PageHeader';
import EmptyState from '../common/EmptyState';
import { STORAGE_KEYS } from '../../constants';

const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
const maxImageSize = 5 * 1024 * 1024;

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ViewEmployeeDetails = () => {
  const { employeId, employeeId } = useParams();
  const selectedEmployeeId = employeeId || employeId;
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openResetConfirmDialog, setOpenResetConfirmDialog] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [statistics, setStatistics] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);

  const currentUser = useMemo(
    () => ({
      userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
      schoolId: localStorage.getItem(STORAGE_KEYS.SCHOOL_ID),
      role: localStorage.getItem(STORAGE_KEYS.USER_ROLE),
    }),
    []
  );

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleError = useCallback(
    (error, fallbackMessage) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === 'string' ? error.response.data : '') ||
        fallbackMessage;
      showSnackbar(message, 'error');
    },
    [showSnackbar]
  );

  const fetchEmployeeDetails = useCallback(async () => {
    if (!selectedEmployeeId) {
      setLoading(false);
      showSnackbar('Employee ID is missing in URL.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/employees/${selectedEmployeeId}`);
      setEmployee(response.data);
    } catch (error) {
      handleError(error, 'Failed to fetch employee details.');
      setEmployee(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEmployeeId, handleError, showSnackbar]);

  const fetchStatistics = useCallback(async () => {
    if (!selectedEmployeeId) return;
    setStatisticsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/teacher-statistics/${selectedEmployeeId}`);
      setStatistics(response.data || null);
    } catch (error) {
      setStatistics(null);
    } finally {
      setStatisticsLoading(false);
    }
  }, [selectedEmployeeId]);

  useEffect(() => {
    fetchEmployeeDetails();
  }, [fetchEmployeeDetails]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!allowedImageTypes.includes(file.type)) {
      showSnackbar('Please upload JPG, PNG, or GIF image.', 'warning');
      return;
    }
    if (file.size > maxImageSize) {
      showSnackbar('Image must be smaller than 5MB.', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('employeeId', selectedEmployeeId);

    setUploading(true);
    try {
      const response = await axiosInstance.post('/api/images/profile/employee/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEmployee((prev) => ({ ...prev, imageUrl: response.data }));
      showSnackbar('Profile image updated.');
    } catch (error) {
      handleError(error, 'Failed to upload profile image.');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmResetPassword = async () => {
    setOpenResetConfirmDialog(false);
    if (!currentUser.userId || !currentUser.schoolId || !currentUser.role) {
      showSnackbar('Admin context missing. Please login again.', 'error');
      return;
    }

    setIsResettingPassword(true);
    try {
      await axiosInstance.post(
        `/api/employees/${selectedEmployeeId}/reset-password-admin`,
        {},
        {
          headers: {
            userId: currentUser.userId,
            schoolId: currentUser.schoolId,
            role: currentUser.role,
          },
        }
      );
      showSnackbar('Password reset successful.');
    } catch (error) {
      handleError(error, 'Failed to reset password.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const canResetPassword = ['SCHOOL_ADMIN', 'ADMIN', 'school_admin', 'admin'].includes(
    String(currentUser.role || '')
  );
  const roleLabel = Array.isArray(employee?.role) ? employee.role.join(', ') : employee?.role || 'N/A';
  const employeeViewModel = useMemo(() => {
    if (!employee) return null;
    return {
      ...employee,
      dateOfBirthLabel: formatDate(employee.dateOfBirth),
      dateOfJoiningLabel: formatDate(employee.dateOfJoining),
      statusLabel:
        employee.status === 1 || String(employee.status).toLowerCase() === 'active'
          ? 'Active'
          : employee.status === 0 || String(employee.status).toLowerCase() === 'inactive'
            ? 'Inactive'
            : employee.status || 'N/A',
    };
  }, [employee]);

  const profileRows = useMemo(
    () => [
      { label: 'Employee Code', value: employeeViewModel?.employeeCode },
      { label: 'Full Name', value: `${employeeViewModel?.firstName || ''} ${employeeViewModel?.lastName || ''}`.trim() },
      { label: 'Designation', value: employeeViewModel?.designation },
      { label: 'Gender', value: employeeViewModel?.gender },
      { label: 'Phone', value: employeeViewModel?.phone },
      { label: 'Email', value: employeeViewModel?.email },
      { label: 'Address', value: employeeViewModel?.address },
      { label: 'Blood Group', value: employeeViewModel?.bloodGroup },
      { label: 'Emergency Contact', value: employeeViewModel?.emergencyContact },
      { label: 'Date of Birth', value: employeeViewModel?.dateOfBirthLabel },
      { label: 'Date of Joining', value: employeeViewModel?.dateOfJoiningLabel },
      { label: 'Status', value: employeeViewModel?.statusLabel },
    ],
    [employeeViewModel]
  );

  const statisticsCards = useMemo(() => {
    if (!statistics) return [];
    const attendanceSummary = statistics.attendanceSummary || {};
    const payrollSummary = statistics.payrollSummary || {};
    const leaveSummary = statistics.leaveSummary || {};
    const achievements = Array.isArray(statistics.achievements) ? statistics.achievements : [];

    return [
      {
        label: 'Attendance',
        value: attendanceSummary.attendancePercentage != null ? `${attendanceSummary.attendancePercentage}%` : 'N/A',
        helper: `${attendanceSummary.presentDays || 0} present / ${attendanceSummary.absentDays || 0} absent`,
        tone: '#2563eb',
      },
      {
        label: 'Payroll',
        value: payrollSummary.latestPayPeriod || 'Not generated',
        helper: payrollSummary.latestTotalSalary != null ? `Latest salary ${Number(payrollSummary.latestTotalSalary || 0).toLocaleString('en-IN')}` : 'No payroll summary yet',
        tone: '#059669',
      },
      {
        label: 'Leave Balance',
        value: leaveSummary.remainingLeaves != null ? leaveSummary.remainingLeaves : 'N/A',
        helper: `${leaveSummary.usedLeaves || 0} used from ${leaveSummary.allocatedLeaves || 0}`,
        tone: '#d97706',
      },
      {
        label: 'Achievements',
        value: achievements.length,
        helper: achievements.length ? 'Recognition records available' : 'No achievements added',
        tone: '#7c3aed',
      },
    ];
  }, [statistics]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!employee) {
    return (
      <PageContainer>
        <EmptyState
          title="Employee details unavailable"
          description="The requested employee could not be loaded."
          action={
            <Button variant="outlined" onClick={() => navigate('/employees')}>
              Back to Employees
            </Button>
          }
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Employee Profile"
        subtitle="Manage employee profile, payroll setup, documents, and bank records from one place."
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate('/employees')}>
              All Employees
            </Button>
            <Button variant="contained" onClick={() => navigate(`/employee/${selectedEmployeeId}/statistics`)}>
              Statistics
            </Button>
          </Stack>
        }
      />

      <Stack spacing={2.5}>
        <EmployeeHeroSection
          employee={employeeViewModel}
          roleLabel={roleLabel}
          uploadLoading={uploading}
          resetLoading={isResettingPassword}
          canResetPassword={canResetPassword}
          uploadAction={
            <>
              {uploading ? <CircularProgress size={14} /> : <UploadFileIcon fontSize="small" sx={{ mr: 0.8 }} />}
              Upload Photo
              <input hidden type="file" accept="image/jpeg,image/png,image/gif" onChange={handleImageUpload} />
            </>
          }
          resetAction={() => setOpenResetConfirmDialog(true)}
          editAction={() => navigate(`/teacher/edit/${selectedEmployeeId}`)}
        />

        <EmployeeProfileTableSection rows={profileRows} />

        <EmployeeWorkSummarySection employee={employeeViewModel} />

        <EmbeddedSection
          title="Statistics Overview"
          subtitle="Quick attendance, payroll, leave, and achievement status without leaving the employee profile."
        >
          {statisticsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : statisticsCards.length ? (
            <Grid container spacing={1.5}>
              {statisticsCards.map((card) => (
                <Grid item xs={12} sm={6} lg={3} key={card.label}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.75,
                      borderRadius: 1.5,
                      height: '100%',
                      borderColor: alpha(card.tone, 0.16),
                      background: `linear-gradient(180deg, ${alpha(card.tone, 0.08)} 0%, rgba(255,255,255,0.98) 100%)`,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ mt: 0.7, fontWeight: 800, color: '#0f172a', fontSize: '1rem' }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.7, color: 'text.secondary' }}>
                      {card.helper}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button variant="contained" onClick={() => navigate(`/employee/${selectedEmployeeId}/statistics`)}>
                    Open Full Statistics
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Statistics are not available for this employee yet.
            </Typography>
          )}
        </EmbeddedSection>

        <EmbeddedSection
          title="Bank Details"
          subtitle="Salary account and banking records used for payroll operations."
        >
        <EmployeeBankDetails employeeId={selectedEmployeeId} showSnackbar={showSnackbar} />
        </EmbeddedSection>

        <EmbeddedSection
          title="Identity Documents"
          subtitle="PAN, Aadhaar, UAN, and ESI details maintained for compliance and payroll."
        >
          <EmployeeIdentifier employeeId={selectedEmployeeId} showSnackbar={showSnackbar} />
        </EmbeddedSection>

        <EmbeddedSection
          title="Salary Assignment"
          subtitle="Assign or review the salary template and package breakdown for this employee."
        >
          <EmployeeSalaryAssignment employeeId={selectedEmployeeId} />
        </EmbeddedSection>

        <Stack direction="row" justifyContent="flex-end">
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Stack>
      </Stack>

      <Dialog open={openResetConfirmDialog} onClose={() => setOpenResetConfirmDialog(false)}>
        <DialogTitle>Reset Employee Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirm password reset for {employee.firstName} {employee.lastName}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetConfirmDialog(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleConfirmResetPassword}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default ViewEmployeeDetails;
